/**
 * Document Loader: the classic interface's side of opening a map. The
 * engine opens it (engine/open-map.js: its key, its registration, loading
 * and syncing it, its tree); this shows it: the progress bar, the screen
 * state this device keeps for the map (focus, view, zoom…), the sidebar,
 * the view, the map's name.
 *
 * Uses callback injection pattern - receives dependencies via init().
 */

import * as LoadProgress from "../ui/load-progress.js";
import { t } from "../../../shared/i18n/index.js";
import { engine } from "../../../engine/index.js";
import { closeMap, opensAskedSoFar } from "../../../engine/open-map.js";
import * as CRDTBridge from "../../../engine/sync/crdt-bridge.js";

// Module state
let callbacks = null;

/**
 * Initialize document loader with callbacks.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getDocState: (docId) => Object|null
 *   - getLastOpenedDoc: () => {docId, timestamp}|null
 *   - setLastOpenedDoc: (docId) => void
 *   - switchView: (viewName) => void
 *   - markSaved: () => void
 *   - VaultStorage: VaultStorage module
 *   - VaultUI: VaultUI module
 *   - Store: Store module
 *   - restoreSidebarState: (docState) => void
 */
export function init(cbs) {
  callbacks = cbs;
}

// The engine's stages of an opening, as the progress bar says them.
const STAGE_TEXT = {
  opening: "app.load.openingMap",
  unlocking: "app.load.unlocking",
  loading: "app.load.loadingMap",
  "reading-local": "app.load.readingLocal",
  "local-loaded": "app.load.localLoaded",
  fetching: "app.load.fetching",
  syncing: "app.load.syncingWithServer",
  building: "app.load.building",
};

function showStage(fraction, stage) {
  LoadProgress.stage(fraction, t(STAGE_TEXT[stage] || "app.load.openingMap"));
}

/**
 * Open a map in the engine and show it. The screen state is set when the
 * engine has the tree, before anything is drawn. Returns whether it opened.
 */
/**
 * The screen state this device keeps for a map (never synced), in the
 * Store with the engine's tree. Returns it (the sidebar's too).
 */
function applyScreenState(tree, docId, { isShared, shareRole, userSetProjectDates }) {
  const localState = callbacks.getDocState(docId) || {};
  callbacks.Store.initialize(tree, {
    // The tree's own id as fallback, not "root" (rootId is dynamic)
    focusId: localState.focusId || tree.id,
    selectedId: null,
    view: localState.currentView || "dashboard",
    hideCompleted: localState.hideCompleted || false,
    ganttScale: localState.ganttScale || "auto",
    ganttZoom: localState.ganttZoom || 1,
    userSetProjectDates,
    docId,
    isShared,
    shareRole,
    mindmapTransform: localState.mindmapTransform,
    bubbleTransform: localState.bubbleTransform,
    notesPanelOpen: localState.notesPanelOpen || false,
    currentNoteId: localState.currentNoteId || null,
  });
  return localState;
}

async function openAndShow(docId, { afterSignIn = false } = {}) {
  let localState = {};
  const opened = await engine.maps.open(docId, {
    // Kept by the callers below: for this tab only after signing in.
    remember: false,
    afterSignIn,
    onProgress: showStage,
    onOpened: (tree, info) => {
      localState = applyScreenState(tree, docId, info);
      LoadProgress.stage(0.9, t("app.load.preparingView"));
    },
  });
  if (!opened) return false;
  // Restore sidebar state (width, collapsed) from local doc state
  callbacks.restoreSidebarState?.(localState);
  callbacks.switchView(callbacks.Store.get("currentView"));
  return true;
}

/**
 * Classic shown again (another interface was shown: interfaces/index.js)
 * over the map the engine has open now, maybe another one: its screen state
 * on this device, the sidebar, the view and the map's name, without opening
 * it again.
 */
export function showOpenMap() {
  const docId = engine.map.id();
  const tree = engine.map.tree();
  if (!docId || !tree) return;
  const localState = applyScreenState(tree, docId, {
    isShared: callbacks.Store.get("isShared"),
    shareRole: callbacks.Store.get("shareRole"),
    userSetProjectDates: CRDTBridge.getMetadata("userSetProjectDates") || false,
  });
  callbacks.restoreSidebarState?.(localState);
  callbacks.switchView(callbacks.Store.get("currentView"));
  callbacks.VaultUI.updateCurrentDocName();
}

// Opens asked since the page started (the vault, a new map, a link, another
// interface through the engine): the start's own choice of map
// (loadInitialDoc) never replaces one opened meanwhile. Opens run one at a
// time (engine/open-map.js), so the start's open, queued after the person's,
// used to win. The engine counts them (every open goes through it).
export { opensAskedSoFar };

/**
 * Handle opening a document.
 */
export async function handleDocOpen(docId) {
  // Progress: no-op when a session is already active (e.g. the post-auth
  // vault bar) — stages then advance that bar instead of starting a new one.
  LoadProgress.beginBar(t("app.load.openingMap"));
  LoadProgress.stage(0.1, t("app.load.openingMap"));
  try {
    if (!await openAndShow(docId)) return;
    callbacks.setLastOpenedDoc(docId);
    callbacks.VaultUI.updateCurrentDocName();
    // Reset delta sync baseline for new doc
    callbacks.markSaved();
  } finally {
    LoadProgress.doneBar();
  }
}

/**
 * Load the initial document after authentication: the one this tab last
 * had open (shared or not), else the most recently updated; nothing when
 * the vault has no map. Unlike handleDocOpen, this tab's last-opened entry
 * is healed for this session only, and a map found empty is said to have
 * maybe not synced before signing out.
 *
 * @returns {Promise<void>}
 */
export async function loadInitialDoc({ since } = {}) {
  // The person opened a map since the sign-in began: theirs stays.
  if (since !== undefined && opensAskedSoFar() !== since) return;
  const lastOpened = callbacks.getLastOpenedDoc();
  const index = callbacks.VaultStorage.getIndex();

  // Find all docs
  const allDocs = index?.docs || [];

  if (!index || allDocs.length === 0) return;

  // Find the doc to open: last opened, or most recently updated. Some
  // entries (legacy shares) are keyed by shareId — match either, or a tab
  // that last had such a doc open falls into the most-recently-updated
  // fallback on every reload.
  let docToOpen = null;
  if (lastOpened?.docId) {
    docToOpen = allDocs.find(d => (d.docId || d.shareId) === lastOpened.docId);
  }
  if (!docToOpen) {
    docToOpen = allDocs.reduce((latest, d) => {
      if (!latest) return d;
      return (d.updatedAt || 0) > (latest.updatedAt || 0) ? d : latest;
    }, null) || allDocs[0];
  }

  if (!docToOpen) return;

  const docId = docToOpen.docId || docToOpen.shareId;
  if (docToOpen._storage === "local") {
    await handleDocOpen(docId);
    return;
  }

  // Usually continues the post-auth vault bar; standalone otherwise.
  LoadProgress.beginBar(t("app.load.openingMap"));
  LoadProgress.stage(0.25, t("app.load.openingYourMap"));
  try {
    if (!await openAndShow(docId, { afterSignIn: true })) return;
    // Heal this tab's last-opened entry. When the entry was stale
    // (deleted doc, missing session) this boot fell back to the most
    // recently UPDATED doc — i.e. whatever another tab or device was
    // editing. Without the heal the stale entry survives, so EVERY
    // reload re-runs that fallback and the tab shadows the other tab's
    // doc forever (both tabs "end up on the same map" after a deploy).
    // Session-only: a fallback choice must not become the cross-tab
    // default that new tabs open.
    callbacks.setLastOpenedDoc(docId, { sessionOnly: true });
  } finally {
    LoadProgress.doneBar();
  }
}

/**
 * Handle deleting a document.
 */
export async function handleDocDelete(docId) {
  const currentDocId = callbacks.VaultStorage.getCurrentDocId();
  if (docId === currentDocId) {
    const index = callbacks.VaultStorage.getIndex();
    const allDocs = index?.docs || [];
    const firstDoc = allDocs.find(d => d.docId !== docId);
    if (firstDoc) {
      callbacks.VaultUI.openPersonalDoc(firstDoc.docId);
    } else {
      console.log("[DocumentLoader] No docs remaining after delete");
      await closeMap();
      callbacks.VaultStorage.setCurrentDocId(null);
    }
  }
}
